import { CaretLeftIcon, CaretRightIcon, DotsThreeIcon } from '@phosphor-icons/react'
import { isFinite } from 'lodash'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/ui/icon'
import { Select } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'

import { Button } from '../button'
import { DEFAULT_LIMIT, getLimitOptions } from './pagination.constants'
import * as S from './pagination.styled'
import { PaginationFilters, PaginationProps } from './pagination.types'
import { getPageItems } from './pagination.utils'

export const Pagination = <T extends PaginationFilters = PaginationFilters>({
  paginationFilters,
  total: rawTotal,
}: PaginationProps<T>) => {
  const { t } = useTranslation(['common'])
  const { filters, setFilter } = paginationFilters
  const { skip = 0, limit = DEFAULT_LIMIT } = filters

  const total = isFinite(rawTotal) ? rawTotal : 0
  const totalPages = Math.ceil(total / limit)
  // Note: Pages are 1-indexed (page 1, 2, 3...), skip is 0-indexed offset
  const currentPage = Math.floor(skip / limit) + 1

  const isFirstPage = currentPage <= 1
  const isLastPage = currentPage >= totalPages

  const handlePrevPage = () => {
    if (!isFirstPage) {
      setFilter('skip', skip - limit)
    }
  }

  const handleNextPage = () => {
    if (!isLastPage) {
      setFilter('skip', skip + limit)
    }
  }

  const handlePageClick = (page: number) => {
    // Note: Convert 1-indexed page to skip offset (page 1 = skip 0, page 2 = skip 50, etc.)
    setFilter('skip', (page - 1) * limit)
  }

  const pageItems = useMemo(() => getPageItems(totalPages, currentPage), [totalPages, currentPage])
  const limitOptions = useMemo(() => getLimitOptions(t), [t])

  const startItem = total === 0 ? 0 : skip + 1
  const endItem = Math.min(skip + limit, total)

  return (
    <S.Footer data-component-id="Pagination">
      <div />

      <S.Controls>
        <Button onlyBox use="ghost" size="small" disabled={isFirstPage} onClick={handlePrevPage}>
          <Icon icon={CaretLeftIcon} />
        </Button>
        {pageItems.map((item) =>
          item === 'left-ellipsis' || item === 'right-ellipsis' ? (
            <Button
              key={item}
              disabled
              onlyBox
              size="small"
              use="ghost"
              aria-label={t('common:pagination.morePages')}
            >
              <Icon icon={DotsThreeIcon} />
            </Button>
          ) : (
            <Button
              key={item}
              onlyBox
              size="small"
              use="secondary"
              active={item === currentPage}
              onClick={() => handlePageClick(item)}
            >
              {item}
            </Button>
          ),
        )}
        <Button onlyBox use="ghost" size="small" disabled={isLastPage} onClick={handleNextPage}>
          <Icon icon={CaretRightIcon} />
        </Button>
      </S.Controls>

      <S.RightSection>
        <Typography color="black500" size="text-small" whiteSpace="nowrap">
          {t('common:pagination.range', { end: endItem, start: startItem, total })}
        </Typography>
        <S.SelectWrapper>
          <Select
            options={limitOptions}
            value={limit}
            size="small"
            onChange={(value) => setFilter('limit', value)}
          />
        </S.SelectWrapper>
      </S.RightSection>
    </S.Footer>
  )
}
